import React, { useState } from 'react';
import { ArrowLeft, User, Phone, Mail, Lock, MailCheck, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import AppLogo from '../components/AppLogo';
import MorphButton from '../components/MorphButton';
import { signUp, login, resendConfirmation, sendPasswordReset } from '../lib/session';

// Inline "Forgot your password?" panel on the Log In tab. Kept on the same
// screen (not a route/modal) so the user can back out to log-in without
// losing their typed email. Uses the same rate-limited /recover endpoint the
// email template is wired for.
//
// SECURITY: we NEVER reveal whether the address has an account — the copy
// after send says "if that address is on IhsanOne, we've sent a reset link"
// regardless of the API result. session.sendPasswordReset() maps "user not
// found" to { ok: true } for the same reason.
function ForgotPasswordPanel({ initialEmail, onBack }) {
  const [email, setEmail] = React.useState(initialEmail || '');
  const [busy, setBusy] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const looksValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());

  const submit = async () => {
    if (busy || !looksValid) return;
    setBusy(true);
    const res = await sendPasswordReset(email.trim());
    setBusy(false);
    if (res.rateLimited) {
      toast.error(res.error, { position: 'top-center' });
      return;
    }
    if (!res.ok) {
      toast.error(res.error || 'Could not send the reset email.', { position: 'top-center' });
      return;
    }
    setSent(true);
  };

  return (
    <div className="px-4 md:px-8 flex flex-col gap-4" data-testid="forgot-password-panel">
      <div>
        <h2 className="text-lg font-extrabold text-[#0F172A]">Reset your password</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Enter your account email and we&apos;ll send you a link to set a new password.
        </p>
      </div>

      {sent ? (
        <div className="rounded-xl bg-primary/5 border border-primary/20 p-4 text-center" data-testid="forgot-password-sent">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-3">
            <MailCheck className="h-6 w-6 text-primary" />
          </div>
          <p className="text-sm font-semibold text-[#0F172A]">Check your email</p>
          <p className="text-xs text-muted-foreground mt-1.5 leading-snug">
            If <span className="font-semibold text-[#0F172A]">{email.trim()}</span> has an IhsanOne account,
            we&apos;ve sent a link to reset your password. It expires in one hour.
          </p>
          <button
            type="button"
            data-testid="forgot-password-back-to-login"
            onClick={onBack}
            className="mt-4 h-10 px-4 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary-hover"
          >
            Back to log in
          </button>
        </div>
      ) : (
        <>
          <div>
            <label className="text-xs font-semibold text-[#0F172A]">Email Address *</label>
            <div className="relative mt-1.5">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                data-testid="forgot-password-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                className="w-full h-11 rounded-xl bg-muted/60 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          </div>
          <button
            type="button"
            data-testid="forgot-password-submit"
            onClick={submit}
            disabled={busy || !looksValid}
            className="w-full h-12 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary-hover disabled:opacity-50"
          >
            {busy ? 'Sending…' : 'Send reset link'}
          </button>
          <p className="text-center text-xs text-muted-foreground">
            Remembered it?{' '}
            <span
              data-testid="forgot-password-cancel"
              onClick={onBack}
              className="text-primary font-semibold cursor-pointer"
            >
              Back to log in
            </span>
          </p>
        </>
      )}
    </div>
  );
}

// Post-signup / pre-login blocker screen when the account is created but the
// user hasn't tapped the confirmation link yet. Shown for BOTH paths:
//   1. Fresh signUp returned needsConfirmation=true (autoconfirm off)
//   2. Login was refused with error_code=email_not_confirmed
// Gives them the address we sent to and a rate-limited resend button.
function CheckYourEmail({ email, onBack }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const handleResend = async () => {
    if (busy) return;
    setBusy(true);
    const res = await resendConfirmation(email);
    setBusy(false);
    if (res.ok) {
      setSent(true);
      toast.success('Confirmation email resent — check your inbox.', { position: 'top-center' });
    } else if (res.rateLimited) {
      toast.error(res.error, { position: 'top-center' });
    } else {
      toast.error(res.error || 'Could not resend right now.', { position: 'top-center' });
    }
  };
  return (
    <div className="px-4 md:px-8 pb-8 pt-4 text-center" data-testid="check-your-email">
      <div className="mx-auto w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-4">
        <MailCheck className="h-7 w-7 text-primary" />
      </div>
      <h2 className="text-lg font-extrabold text-[#0F172A]">Check your email</h2>
      <p className="text-sm text-muted-foreground mt-2 leading-snug">
        We sent a confirmation link to<br />
        <span className="font-semibold text-[#0F172A]">{email}</span>.<br />
        Tap the link (or enter the 6-digit code) to activate your account, then log in.
      </p>
      <button
        type="button"
        data-testid="resend-confirmation-button"
        onClick={handleResend}
        disabled={busy}
        className="mt-6 h-11 px-5 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary-hover disabled:opacity-50"
      >
        {busy ? 'Sending…' : sent ? 'Resend confirmation email' : 'Resend confirmation email'}
      </button>
      <p className="mt-4 text-xs text-muted-foreground">
        Wrong address?{' '}
        <span data-testid="check-your-email-change" onClick={onBack} className="text-primary font-semibold cursor-pointer">
          Use a different email
        </span>
      </p>
    </div>
  );
}

// Password field with a reveal toggle. Matches the plain inputs on this form
// but reserves right padding for the eye button.
function PasswordInput({ testId, value, onChange, placeholder, show, onToggle, label }) {
  return (
    <div className="relative mt-1.5">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <input
        data-testid={testId}
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full h-11 rounded-xl bg-muted/60 pl-9 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
      />
      <button
        type="button"
        data-testid={`${testId}-toggle`}
        onClick={onToggle}
        aria-label={show ? `Hide ${label}` : `Show ${label}`}
        className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-[#0F172A]"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export default function VolunteerSignup({ onBack, onSubmit }) {
  const [mode, setMode] = useState('signup'); // 'signup' | 'login' | 'forgot'
  const [fullName, setFullName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pdpaConsent, setPdpaConsent] = useState(false);
  const [updatesConsent, setUpdatesConsent] = useState(false);
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  // When signup succeeds but Supabase withheld the access_token (autoconfirm
  // off), or login fails with email_not_confirmed, we swap the form for a
  // "check your email" screen with a resend button. The address is captured
  // so resend/back-out work without re-typing.
  const [pendingConfirm, setPendingConfirm] = useState(null); // { email } | null

  // Record the session before handing control back, so no later flow re-gates.
  const completeAuth = () => {
    onSubmit && onSubmit();
  };

  // Validation runs on click; MorphButton only plays the success wash and
  // calls onSubmit (via onComplete) once these pass — no toast needed for
  // the happy path anymore, the button itself confirms the action.
  const validateCreateAccount = () => {
    if (!fullName.trim() || !mobile.trim() || !email.trim() || !password.trim()) {
      toast.error('Please fill in all required fields', { position: 'top-center' });
      return false;
    }
    if (password.trim().length < 6) {
      toast.error('Password must be at least 6 characters', { position: 'top-center' });
      return false;
    }
    if (confirmPassword !== password) {
      toast.error("Passwords don't match", { position: 'top-center' });
      return false;
    }
    if (!pdpaConsent) {
      toast.error('Please agree to the PDPA Consent to continue', { position: 'top-center' });
      return false;
    }
    return true;
  };

  const validateLogin = () => {
    if (!loginIdentifier.trim() || !loginPassword.trim()) {
      toast.error('Please enter your mobile number/email and password', { position: 'top-center' });
      return false;
    }
    return true;
  };

  return (
    <div className="min-h-screen bg-background md:bg-[#F3F6F5] page-fade-in" data-testid="volunteer-signup-page">
      <div className="bg-white px-4 pt-4 pb-3 flex items-center gap-3 border-b border-border/60">
        <button type="button" data-testid="signup-back-button" onClick={onBack} aria-label="Back">
          <ArrowLeft className="h-5 w-5 text-[#0F172A]" />
        </button>
        <AppLogo size="sm" />
        <span className="text-base font-extrabold leading-none">
          <span className="text-[#0F172A]">Ihsan</span>
          <span className="text-primary">One</span>
        </span>
      </div>

      {/* On desktop this whole form renders as a centered card, like a typical
          web sign-up modal, instead of stretching the mobile layout full-width. */}
      <div className="md:max-w-[440px] md:mx-auto md:my-10 md:bg-white md:rounded-2xl md:shadow-lg md:border md:border-border/60 md:pb-2">
      <div className="px-4 pt-5 pb-3 md:px-8">
        <h1 className="text-2xl font-extrabold text-[#0F172A]">
          {mode === 'signup' ? 'Join Us' : mode === 'forgot' ? 'Reset Password' : 'Welcome Back'}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {mode === 'signup'
            ? 'Create your account to start making a difference in your community.'
            : mode === 'forgot'
              ? 'We’ll email you a link to set a new password.'
              : 'Log in to continue where you left off.'}
        </p>
      </div>

      {mode !== 'forgot' && (
        <div className="px-4 pb-4 md:px-8">
          <div className="flex bg-muted rounded-xl p-1">
            <button
              type="button"
              data-testid="auth-mode-signup"
              onClick={() => setMode('signup')}
              className={`flex-1 h-9 rounded-lg text-sm font-semibold transition-colors ${
                mode === 'signup' ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground'
              }`}
            >
              Sign Up
            </button>
            <button
              type="button"
              data-testid="auth-mode-login"
              onClick={() => setMode('login')}
              className={`flex-1 h-9 rounded-lg text-sm font-semibold transition-colors ${
                mode === 'login' ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground'
              }`}
            >
              Log In
            </button>
          </div>
        </div>
      )}

      {pendingConfirm ? (
        <CheckYourEmail
          email={pendingConfirm.email}
          onBack={() => setPendingConfirm(null)}
        />
      ) : mode === 'forgot' ? (
        <ForgotPasswordPanel
          initialEmail={loginIdentifier}
          onBack={() => setMode('login')}
        />
      ) : mode === 'signup' ? (
        <>
          <div className="px-4 md:px-8 flex flex-col gap-4">
            <div>
              <label className="text-xs font-semibold text-[#0F172A]">Full Name *</label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  data-testid="signup-fullname-input"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="e.g. Nur Aisyah Binte Ahmad"
                  className="w-full h-11 rounded-xl bg-muted/60 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#0F172A]">Mobile Number *</label>
              <div className="mt-1.5 flex items-center gap-2">
                <span className="flex items-center gap-1 h-11 px-3 rounded-xl bg-muted/60 text-sm shrink-0">🇸🇬 +65</span>
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    data-testid="signup-mobile-input"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder="8123 4567"
                    className="w-full h-11 rounded-xl bg-muted/60 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#0F172A]">Email Address *</label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  data-testid="signup-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full h-11 rounded-xl bg-muted/60 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#0F172A]">Password *</label>
              <PasswordInput
                testId="signup-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password (min. 6 characters)"
                show={showPassword}
                onToggle={() => setShowPassword((s) => !s)}
                label="password"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#0F172A]">Confirm Password *</label>
              <PasswordInput
                testId="signup-confirm-password-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                show={showConfirmPassword}
                onToggle={() => setShowConfirmPassword((s) => !s)}
                label="confirm password"
              />
              {confirmPassword.length > 0 && confirmPassword !== password && (
                <p className="mt-1.5 text-xs text-destructive">Passwords don&apos;t match yet.</p>
              )}
            </div>
          </div>

          <div className="px-4 md:px-8 pt-4">
            <div className="flex items-start gap-2.5 rounded-xl bg-primary/5 border border-primary/20 p-3" data-testid="email-verify-note">
              <MailCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-muted-foreground leading-snug">
                <span className="font-semibold text-[#0F172A]">Verify your email to activate your account.</span>{' '}
                We&apos;ll send a verification link to your email — please confirm it to become an account holder on IhsanOne.
              </p>
            </div>
          </div>

          <div className="px-4 md:px-8 pt-5">
            <h2 className="text-sm font-bold text-[#0F172A] mb-3">Your Consent</h2>
            <label className="flex items-start gap-3 mb-4 cursor-pointer">
              <input
                type="checkbox"
                data-testid="signup-pdpa-checkbox"
                checked={pdpaConsent}
                onChange={(e) => setPdpaConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-primary"
              />
              <span className="text-xs text-muted-foreground leading-snug">
                <span className="font-semibold text-[#0F172A]">I agree to the PDPA Consent</span><br />
                I understand that my personal data will be used in accordance with MTFA&apos;s PDPA policy for volunteer management and communication.
                <br /><span className="text-primary font-semibold">View PDPA Policy</span>
              </span>
            </label>
            <label className="flex items-start gap-3 mb-6 cursor-pointer">
              <input
                type="checkbox"
                data-testid="signup-updates-checkbox"
                checked={updatesConsent}
                onChange={(e) => setUpdatesConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-primary"
              />
              <span className="text-xs text-muted-foreground leading-snug">
                <span className="font-semibold text-[#0F172A]">I want to receive updates</span><br />
                I would like to receive updates on volunteer opportunities, events and community news from IhsanOne (via email and/or WhatsApp).
                <br />You can unsubscribe anytime.
              </span>
            </label>
          </div>

          <div className="px-4 md:px-8 pb-3">
            <MorphButton
              data-testid="create-account-button"
              idleLabel="Create Account"
              successLabel="Account Created"
              onClick={async () => {
                if (!validateCreateAccount()) return false;
                const { error, needsConfirmation } = await signUp(email.trim(), password, {
                  full_name: fullName.trim(),
                  mobile: mobile.trim(),
                });
                if (error) { toast.error(error, { position: 'top-center' }); return false; }
                if (needsConfirmation) {
                  // Autoconfirm off — swap the form for the "check your email"
                  // screen. Return false so MorphButton doesn't fire onComplete
                  // (which would close the gate before the user confirms).
                  setPendingConfirm({ email: email.trim() });
                  return false;
                }
                return true;
              }}
              onComplete={() => completeAuth()}
              className="w-full h-12 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary-hover"
            />
          </div>
          <p className="text-center text-xs text-muted-foreground pb-8">
            Already have an account?{' '}
            <span data-testid="switch-to-login-link" onClick={() => setMode('login')} className="text-primary font-semibold cursor-pointer">
              Log In
            </span>
          </p>
        </>
      ) : (
        <div className="px-4 md:px-8 flex flex-col gap-4">
          <div>
            <label className="text-xs font-semibold text-[#0F172A]">Mobile Number or Email *</label>
            <div className="relative mt-1.5">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                data-testid="login-identifier-input"
                value={loginIdentifier}
                onChange={(e) => setLoginIdentifier(e.target.value)}
                placeholder="8123 4567 or you@example.com"
                className="w-full h-11 rounded-xl bg-muted/60 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          </div>
          <div>
            <div className="flex items-baseline justify-between">
              <label className="text-xs font-semibold text-[#0F172A]">Password *</label>
              <button
                type="button"
                data-testid="forgot-password-link"
                onClick={() => setMode('forgot')}
                className="text-xs font-semibold text-primary hover:underline"
              >
                Forgot password?
              </button>
            </div>
            <PasswordInput
              testId="login-password-input"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              placeholder="Enter your password"
              show={showLoginPassword}
              onToggle={() => setShowLoginPassword((s) => !s)}
              label="password"
            />
          </div>
          <MorphButton
            data-testid="login-button"
            idleLabel="Log In"
            successLabel="Welcome Back"
            onClick={async () => {
              if (!validateLogin()) return false;
              const { error, needsConfirmation } = await login(loginIdentifier.trim(), loginPassword);
              if (error) {
                toast.error(error, { position: 'top-center' });
                if (needsConfirmation) {
                  // Account exists but unconfirmed — swap to the resend screen
                  // so they can trigger a fresh email without re-entering the
                  // password. Return false so MorphButton doesn't fire the
                  // success wash on an unfinished sign-in.
                  setPendingConfirm({ email: loginIdentifier.trim() });
                }
                return false;
              }
              return true;
            }}
            onComplete={() => completeAuth()}
            className="w-full h-12 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary-hover mt-2"
          />
          <p className="text-center text-xs text-muted-foreground pb-8">
            Don&apos;t have an account?{' '}
            <span data-testid="switch-to-signup-link" onClick={() => setMode('signup')} className="text-primary font-semibold cursor-pointer">
              Sign Up
            </span>
          </p>
        </div>
      )}
      </div>
    </div>
  );
}
